import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Video, X, CheckCircle2 } from "lucide-react";
import { VirtualMeetingButton } from "./VirtualMeetingButton";

/**
 * Welcome strip shown on /account only when the URL carries `?welcome=jitsi`
 * (set by BuyerRegister / SellerRegister on successful registration).
 * Reinforces Jitsi as the preferred meeting brand and offers an optional
 * one-tap verification-call request — Zoho Meeting & Google Meet are
 * available on request via the admin panel.
 */
export function WelcomeJitsiBanner({ api }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    if (q.get("welcome") === "jitsi") setVisible(true);
  }, [loc.search]);

  const dismiss = () => {
    setVisible(false);
    const q = new URLSearchParams(loc.search);
    q.delete("welcome");
    nav({ pathname: loc.pathname, search: q.toString() ? `?${q.toString()}` : "" }, { replace: true });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="welcome-jitsi-banner"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 relative overflow-hidden rounded-sm border border-[#2FB999]/40 bg-[#2FB999]/[0.05]"
        >
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#2FB999] to-transparent opacity-70" />
          <button
            data-testid="welcome-jitsi-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute top-3 right-3 text-[#8A8A82] hover:text-[#F5F5F0] p-1"
          >
            <X size={16} />
          </button>
          <div className="p-5 md:p-6 flex flex-wrap items-start gap-5">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#2FB999] text-black shrink-0">
              <CheckCircle2 size={19} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#2FB999]">
                Welcome aboard · Optional next step
              </div>
              <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-1">
                Book an optional verification call on Jitsi.
              </div>
              <p className="font-body text-sm text-[#C9C9C2] mt-2 max-w-2xl leading-relaxed">
                Vinci uses <b className="text-[#F5F5F0]">Jitsi</b> as the preferred, browser-native meeting
                room for every partner — no download, encrypted end-to-end, and every recording lands
                straight in your legal archive.
                <span className="text-[#8A8A82]"> Zoho Meeting & Google Meet remain available on request.</span>
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <VirtualMeetingButton api={api} />
                <button
                  data-testid="welcome-jitsi-skip"
                  onClick={dismiss}
                  className="rounded-full border border-white/20 hover:border-white/40 text-[#C9C9C2] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]"
                >
                  Skip for now
                </button>
                <span className="inline-flex items-center gap-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
                  <Video size={11} /> Preferred: Jitsi · Zoho · Google
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
